import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { faCircleMinus } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "antd/es/tooltip";
import { defineMessages, useIntl } from "react-intl";

import { isDefined } from "@mapmycustomers/shared/util/assert";
import userDisplayName from "@mapmycustomers/shared/util/formatters/userDisplayName";
import { LoadingSpinner, Popconfirm } from "@mapmycustomers/ui";

import Trend from "@app/component/Trend";
import { isManager, isOwner } from "@app/store/iam/util";
import { getTeams, getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import { UserPreviewCard } from "@app/types/dashboard/DashboardCard";
import Scope from "@app/types/dashboard/Scope";

import { fetchCardData, updateCards } from "../store/actions";
import { CardData } from "../store/saga";
import getChartTitle from "../util/getChartTitle";

import styles from "./DataCard.module.scss";

const messages = defineMessages({
  cancelText: {
    id: "userPreviewCard.dataCard.cancelText",
    defaultMessage: "Cancel",
    description: "Remove Metric tooltip title",
  },
  organization: {
    id: "userPreviewCard.dataCard.subtitle.organization",
    defaultMessage: "Organization",
    description: "Organization title for the case when ranking a team for Subtitle of the card",
  },
  removeMetric: {
    id: "userPreviewCard.dataCard.removeMetric",
    defaultMessage: "Remove Metric",
    description: "Remove Metric title",
  },
  removeMetricTitle: {
    id: "userPreviewCard.dataCard.removeMetric.title",
    defaultMessage: "Remove this metric for all Team Members in “{teamName}”?",
    description: "Remove Metric popconfirm title",
  },
  subtitle: {
    id: "userPreviewCard.dataCard.subtitle",
    defaultMessage:
      "{entireOrg, select, true {All Users} other {{name}}}{showRank, select, true { • {rank, selectordinal, one {#st} two {#nd} few {#rd} other {#th}} in {teamName}} other {}}",
    description: "Subtitle of the card",
  },
  unknown: {
    id: "userPreviewCard.dataCard.subtitle.unknown",
    defaultMessage: "Unknown",
    description:
      "Unknown title for the case when ranking a team for Subtitle of the Activities Logged card",
  },
});

interface DataCardProps {
  allowRemove?: boolean;
  card: UserPreviewCard;
  index?: number;
  scope: Scope;
}

const DataCard = ({
  allowRemove = true,
  card,
  index,
  onFetchData,
  onUpdateCards,
  scope,
  teams,
  users,
}: DataCardProps & typeof mapDispatchToProps & ReturnType<typeof mapStateToProps>) => {
  const intl = useIntl();
  const [data, setData] = useState<CardData | undefined>();
  const [showPopconfirm, setShowPopconfirm] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  const { teamId, userId } = scope;

  const user = useMemo(() => users.find(({ id }) => id === userId), [userId, users]);
  const team = useMemo(() => teams.find(({ id }) => id === teamId), [teamId, teams]);
  const isOrgScope = user && isOwner(user);
  const isTeamScope = user && isManager(user);
  const unknown = <i>{intl.formatMessage(messages.unknown)}</i>;

  const trend =
    data && data.previousCount ? (data.count - data.previousCount) / data.previousCount : undefined;

  const handleRemove = useCallback(() => {
    if (index !== undefined) {
      onUpdateCards({ cardIds: { [index]: undefined }, scope });
    }
    setShowPopconfirm(false);
  }, [index, onUpdateCards, scope]);

  const handleCancel = useCallback(() => setShowPopconfirm(false), []);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (teamId) {
        setShowPopconfirm(open);
      }
      if (open) {
        setShowTooltip(false);
      }
    },
    [teamId]
  );

  const handleTooltipOpen = useCallback((open: boolean) => setShowTooltip(open), []);

  useEffect(() => {
    onFetchData({
      callback: setData,
      card,
      scope: {
        teamId: user && !isOwner(user) ? teamId : undefined,
        userId: user && !isManager(user) && !isOwner(user) ? userId : undefined,
      },
    });
  }, [card, onFetchData, teamId, user, userId]);

  useEffect(() => {
    setData(undefined);
  }, [card.id]);

  return !data ? (
    <LoadingSpinner />
  ) : (
    <div className={styles.container}>
      {allowRemove && (
        <Popconfirm
          cancelText={intl.formatMessage(messages.cancelText)}
          description={intl.formatMessage(messages.removeMetricTitle, { teamName: team?.name })}
          getPopupContainer={(trigger: HTMLElement) => trigger.parentElement?.parentElement!}
          okText={intl.formatMessage(messages.removeMetric)}
          onCancel={handleCancel}
          onConfirm={handleRemove}
          onOpenChange={handleOpenChange}
          open={showPopconfirm}
          overlayClassName={styles.popconfirm}
          trigger="click"
        >
          <Tooltip
            onOpenChange={handleTooltipOpen}
            open={showTooltip}
            title={intl.formatMessage(messages.removeMetric)}
          >
            <FontAwesomeIcon
              className={styles.removeIcon}
              icon={faCircleMinus}
              onClick={!teamId ? handleRemove : undefined}
              size="lg"
            />
          </Tooltip>
        </Popconfirm>
      )}
      <div className={styles.titleContainer}>
        <div className={styles.title}>{getChartTitle(intl, card)}</div>
        <div className={styles.subtitle}>
          {intl.formatMessage(messages.subtitle, {
            entireOrg: isOrgScope,
            name: (isTeamScope ? team?.name : user ? userDisplayName(user) : undefined) ?? unknown,
            rank: data.rank,
            showRank: !isOrgScope && isDefined(data.rank),
            teamName: isTeamScope
              ? intl.formatMessage(messages.organization)
              : (team?.name ?? unknown),
          })}
        </div>
      </div>
      <div className={styles.content}>
        <span className={styles.count}>
          {intl.formatNumber(data.count, {
            maximumFractionDigits: 2,
            notation: data.count >= 10000 ? "compact" : "standard",
          })}
        </span>
        <Trend trend={trend} />
      </div>
    </div>
  );
};

const mapDispatchToProps = {
  onFetchData: fetchCardData.request,
  onUpdateCards: updateCards.request,
};

const mapStateToProps = (state: RootState) => ({
  teams: getTeams(state),
  users: getUsers(state),
});

export default connect(mapStateToProps, mapDispatchToProps)(DataCard);
